import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Grid,
  Group,
  Loader,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip
} from '@mantine/core';
import { IconFileSpreadsheet, IconRefresh, IconSearch } from '@tabler/icons-react';
import type { MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import { useGetSupplyStockReport } from '../../../hooks/reports/queries';
import { useGetStores } from '../../../hooks/stores/queries';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { exportSupplyStockReport } from '../../../services/reports.service';
import { hasPermission } from '../../../utils/permission-utils';
import TableView from './TableView';

const PAGE_SIZE = 25;
type ReportUser = {
  store?: string | { _id?: string };
  permission?: Record<string, { canView?: boolean }>;
};

function SupplyStockReportContent({ user }: { user: ReportUser }) {
  const { t, i18n } = useTranslation(['ReportsPage', 'CommonButtons']);
  const location = useLocation();
  const activeStoreId = new URLSearchParams(location.search).get('storeId');
  const userStoreId = typeof user.store === 'string' ? user.store : user.store?._id;
  const canViewAll = hasPermission(user, 'allowMultiStoreView', 'canView');
  const {
    data: stores = [],
    isLoading: storesLoading,
    isError: storesError,
    refetch: refetchStores
  } = useGetStores(true);
  const [filters, setFilters] = useState({ store: '', search: '' });
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE
  });
  const [exportError, setExportError] = useState(false);
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const defaultStore =
    stores.find((store) => store._id === activeStoreId) ||
    stores.find((store) => store._id === userStoreId) ||
    stores[0];
  const storeId =
    storesError || !stores.length
      ? ''
      : filters.store === 'all' && canViewAll
      ? 'all'
      : stores.find((store) => store._id === filters.store)?._id || defaultStore?._id || '';
  const query = {
    store: storeId,
    search: filters.search,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize
  };
  const { data, isLoading, isFetching, isError, refetch } = useGetSupplyStockReport(query);
  const loading = storesLoading || (Boolean(storeId) && (isLoading || isFetching));
  const dateFormat = new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
  const storeOptions = stores.flatMap((store) =>
    store._id ? [{ value: store._id, label: store.name || store._id }] : []
  );
  if (canViewAll && stores.length) {
    storeOptions.unshift({ value: 'all', label: t('ReportsPage:supplyStockReport.allStores') });
  }

  const handleExport = async () => {
    setExportError(false);
    const storeName =
      storeId === 'all' ? 'Todas' : stores.find((store) => store._id === storeId)?.name || storeId;
    const safeName = storeName.replace(/[^\p{L}\p{N}_-]+/gu, '_');
    try {
      await downloadExcelV2(
        () => exportSupplyStockReport({ store: storeId, search: filters.search }),
        `Existencias_Insumos_${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch {
      setExportError(true);
    }
  };

  return (
    <CatalogContainer style={{ minWidth: 0, maxWidth: '100%' }}>
      <Title order={1} mb="sm">
        {t('ReportsPage:supplyStockReport.title')}
      </Title>
      <Text c="dimmed" mb="lg">
        {t('ReportsPage:supplyStockReport.description')}
      </Text>
      <Grid align="end" mb="md">
        <Grid.Col span={{ base: 12, sm: 5, lg: 4 }}>
          <Select
            label={t('ReportsPage:supplyStockReport.store')}
            data={storeOptions}
            value={storeId || null}
            disabled={storesLoading || storesError || !stores.length}
            allowDeselect={false}
            searchable
            onChange={(store) => {
              setFilters((current) => ({ ...current, store: store || '' }));
              setPagination((current) => ({ ...current, pageIndex: 0 }));
              setExportError(false);
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 7, lg: 4 }}>
          <TextInput
            label={t('ReportsPage:supplyStockReport.search')}
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(event) => {
              const search = event.currentTarget.value;
              setFilters((current) => ({ ...current, search }));
              setPagination((current) => ({ ...current, pageIndex: 0 }));
              setExportError(false);
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Group gap="xs">
            <Tooltip label={t('ReportsPage:supplyStockReport.refresh')}>
              <ActionIcon
                variant="default"
                disabled={loading || !storeId}
                onClick={() => refetch()}
                aria-label={t('ReportsPage:supplyStockReport.refresh')}
              >
                <IconRefresh style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('CommonButtons:exportList')}>
              <ActionIcon
                variant="filled"
                color="#1D6F42"
                loading={loadingExport}
                disabled={loadingExport || !storeId || storesLoading || storesError}
                onClick={handleExport}
                aria-label={t('CommonButtons:exportList')}
              >
                <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Grid.Col>
      </Grid>
      {exportError && (
        <Alert color="red" mb="md" role="alert">
          {t('ReportsPage:supplyStockReport.exportError')}
        </Alert>
      )}
      {loading && (
        <Group role="status" mb="md">
          <Loader size="sm" />
          <Text>{t('ReportsPage:supplyStockReport.loading')}</Text>
        </Group>
      )}
      {storesError || isError ? (
        <Alert color="red" role="alert">
          <Text mb="sm">{t('ReportsPage:supplyStockReport.loadError')}</Text>
          <Button variant="light" onClick={() => (storesError ? refetchStores() : refetch())}>
            {t('ReportsPage:supplyStockReport.retry')}
          </Button>
        </Alert>
      ) : !storesLoading && !stores.length ? (
        <Text>{t('ReportsPage:supplyStockReport.noStores')}</Text>
      ) : (
        <>
          {data && (
            <Text c="dimmed" size="sm" mb="sm">
              {t('ReportsPage:supplyStockReport.queriedAt', {
                date: dateFormat.format(new Date(data.queriedAt))
              })}
            </Text>
          )}
          <TableView
            tableInfoData={data?.items || []}
            totalRowsCount={data?.total || 0}
            isLoading={loading}
            isError={isError}
            pagination={pagination}
            setPagination={setPagination}
          />
        </>
      )}
    </CatalogContainer>
  );
}

export default function SupplyStockReport() {
  const { user } = useSelector((state: { user: { user: ReportUser } }) => state.user);
  const { t } = useTranslation('ReportsPage');
  if (!hasPermission(user, 'reports', 'canView') || !hasPermission(user, 'inventory', 'canView')) {
    return (
      <Alert color="red" role="alert">
        {t('ReportsPage:supplyStockReport.notAuthorized')}
      </Alert>
    );
  }
  return <SupplyStockReportContent user={user} />;
}
